import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiPlay, FiSave, FiAlertTriangle, FiCheckCircle, FiCalendar, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import usePagePermissions from '../../hooks/usePagePermissions';
import PageLayout from '../../components/PageLayout';
import {
  ContentCard, StatsRow, StatCard, StatIcon, StatInfo, StatValue, StatLabel,
  Table, Th, Tr, Td, SaveButton, FormGroup, Label, Select,
} from '../../components/SharedStyles';
import { SYSTEM_ACCOUNTS } from '../../config/tranTypes';
import { buildDepreciationRun } from '../../utils/depreciationHelper';
import { findAccountByCode } from '../../utils/journalEntryHelper';
import {
  loadFixedAssets, loadFixedAssetCategories, findExistingDepreciationRun,
  postDepreciationRun, applyDepreciationToAssets,
  loadDepreciationRuns, updateDepreciationRun, deleteDepreciationRun,
} from '../../utils/fixedAssetHelper';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const lastDayOf = (year, month1to12) => {
  const d = new Date(year, month1to12, 0);
  return d.toISOString().split('T')[0];
};
// Asset Appreciation: share of an (edited) amount that falls on the appreciation
// (never above the appreciation still left to depreciate).
const apprPartOf = (r) => ((r.calcPart > 0 && r.calcAmount > 0)
  ? Math.max(0, Math.min(r.editAmount, r.partCap != null ? r.partCap : r.calcPart, round2(r.calcPart * (r.editAmount / r.calcAmount))))
  : 0);

const DepreciationRun = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('fixedAssets', 'depreciation');
  const { formatCurrency } = useCurrency();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [frequency, setFrequency] = useState('monthly');

  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState(null); // null = not generated yet
  const [existingRun, setExistingRun] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [posting, setPosting] = useState(false);
  // ACC-23: posted runs can be edited or deleted.
  const [runs, setRuns] = useState([]);
  const [editingRun, setEditingRun] = useState(null);
  const [editDate, setEditDate] = useState('');

  const loadCats = useCallback(async () => {
    if (!activeCompany?.id) return;
    setCategories(await loadFixedAssetCategories(activeCompany.id));
  }, [activeCompany]);
  useEffect(() => { loadCats(); }, [loadCats]);

  const loadRuns = useCallback(async () => {
    if (!activeCompany?.id) return;
    try { setRuns(await loadDepreciationRuns(activeCompany.id)); } catch (e) { console.error('Load runs failed:', e); }
  }, [activeCompany]);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const startEditRun = async (run) => {
    try {
      const assets = await loadFixedAssets(activeCompany.id);
      const byId = {}; assets.forEach(a => { byId[a.id] = a; });
      const editRows = (run.depreciationSnapshot || []).filter(r => r.assetId).map(r => {
        const a = byId[r.assetId] || {};
        // Book basis: cost + any appreciation (0 for most assets).
        const cost = round2((Number(a.acquisitionCost) || 0) + (Number(a.appreciationAmount) || 0));
        const accumBefore = round2((Number(a.accumulatedDepreciation) || 0) - (Number(r.amount) || 0));
        const amt = round2(r.amount);
        return {
          assetId: r.assetId, assetCode: r.assetCode || a.assetCode, assetName: r.assetName || a.assetName,
          categoryId: a.categoryId, categoryName: a.categoryName, method: a.depreciationMethod,
          cost, accumBefore, opening: round2(cost - accumBefore),
          amount: amt, editAmount: amt, include: true,
          newAccumulated: round2(accumBefore + amt), newNBV: round2(cost - accumBefore - amt),
          disposed: a.status === 'Disposed',
        };
      });
      setEditingRun(run);
      setEditDate(run.docDate || '');
      setExistingRun(null);
      setRows(editRows);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert(`Could not open this run: ${e.message}`);
    }
  };

  const cancelEditRun = () => { setEditingRun(null); setRows(null); };

  const handleDeleteRun = async (run) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete depreciation run ${run.docNumber} (${run.periodLabel || run.periodKey})?\n\nIts accounting entry is removed and the depreciation is given back to each asset.`)) return;
    try {
      await deleteDepreciationRun(activeCompany.id, run, currentUser.uid);
      if (editingRun?.id === run.id) cancelEditRun();
      await loadRuns();
      alert(`Deleted ${run.docNumber}.`);
    } catch (e) {
      alert(`Could not delete: ${e.message}`);
    }
  };

  const periodKey = frequency === 'yearly' ? String(year) : `${year}-${String(month).padStart(2, '0')}`;
  const periodLabel = frequency === 'yearly' ? `Year ${year}` : `${MONTHS[month - 1]} ${year}`;
  const months = frequency === 'yearly' ? 12 : 1;
  const asOfDate = frequency === 'yearly' ? `${year}-12-31` : lastDayOf(year, month);

  const handleGenerate = async () => {
    setGenerating(true);
    setRows(null);
    try {
      const [assets, existing] = await Promise.all([
        loadFixedAssets(activeCompany.id),
        findExistingDepreciationRun(activeCompany.id, periodKey),
      ]);
      setExistingRun(existing);
      const active = assets.filter(a => a.status === 'Active');
      const { lines } = buildDepreciationRun(active, { months, asOfDate });
      // Keep only depreciable rows; carry editable amount + cost basis.
      const editable = lines
        .filter(l => l.amount > 0 || (!l.skipped || l.skipped === 'zero'))
        .map(l => ({
          ...l,
          accumBefore: round2(l.newAccumulated - l.amount),
          cost: round2(l.opening + (l.newAccumulated - l.amount)),
          include: l.amount > 0,
          editAmount: l.amount,
          // Only set for assets that have appreciation.
          ...(l.newAppreciationDepreciation != null ? {
            calcAmount: l.amount,
            calcPart: l.appreciationPart || 0,
            partCap: l.appreciationPartCap,
            apprDepBefore: round2(l.newAppreciationDepreciation - (l.appreciationPart || 0)),
          } : {}),
        }))
        .filter(l => l.amount > 0); // only assets that actually depreciate this period
      setRows(editable);
    } catch (e) {
      console.error('Generate failed:', e);
      alert('Failed to generate depreciation. Ensure categories are seeded.');
    } finally {
      setGenerating(false);
    }
  };

  const setRowAmount = (assetId, val) => {
    const amt = Math.max(0, round2(val));
    setRows(prev => prev.map(r => {
      if (r.assetId !== assetId) return r;
      const newNBV = round2(r.cost - (r.accumBefore + amt));
      // r.floor is only set for assets with appreciation (salvage + appreciation that can't be depreciated).
      return { ...r, editAmount: amt, newAccumulated: round2(r.accumBefore + amt), newNBV, fullyDepreciated: newNBV <= (r.floor != null ? r.floor : 0) + 0.01, amount: amt };
    }));
  };

  const toggleInclude = (assetId) => {
    setRows(prev => prev.map(r => r.assetId === assetId ? { ...r, include: !r.include } : r));
  };

  const includedRows = (rows || []).filter(r => r.include && r.editAmount > 0);
  const grandTotal = round2(includedRows.reduce((s, r) => s + r.editAmount, 0));

  // Group by category for the Cr legs + display.
  const byCategory = {};
  includedRows.forEach(r => {
    const k = r.categoryId || 'uncat';
    if (!byCategory[k]) byCategory[k] = { categoryId: r.categoryId, categoryName: r.categoryName, amount: 0 };
    byCategory[k].amount = round2(byCategory[k].amount + r.editAmount);
  });

  const handleSaveEdit = async () => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    if (grandTotal <= 0) { alert('All amounts are zero — delete the run instead.'); return; }
    if (!window.confirm(`Save changes to ${editingRun.docNumber}? New total ${formatCurrency(grandTotal)}.`)) return;
    setPosting(true);
    try {
      await updateDepreciationRun(activeCompany.id, editingRun, {
        rows: includedRows.map(r => ({ assetId: r.assetId, amount: r.editAmount })),
        docDate: editDate || editingRun.docDate,
        categories,
        userId: currentUser.uid,
      });
      alert(`Updated ${editingRun.docNumber}.`);
      cancelEditRun();
      await loadRuns();
    } catch (e) {
      console.error('Update run failed:', e);
      alert(`Failed to update: ${e.message}`);
    } finally {
      setPosting(false);
    }
  };

  const handlePost = async () => {
    if (editingRun) return handleSaveEdit();
    if (!canEdit) { alert('You do not have permission to post.'); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year.'); return; }
    if (includedRows.length === 0 || grandTotal <= 0) { alert('Nothing to post.'); return; }
    if (existingRun) { if (!window.confirm(`A depreciation run already exists for ${periodLabel}. Post another?`)) return; }
    if (!window.confirm(`Post depreciation of ${formatCurrency(grandTotal)} for ${periodLabel}?`)) return;

    setPosting(true);
    try {
      const depExpenseAccount = await findAccountByCode(activeCompany.id, SYSTEM_ACCOUNTS.DEPRECIATION_EXPENSE);
      const categoryLines = Object.values(byCategory).map(c => {
        const cat = categories.find(cc => cc.id === c.categoryId);
        return {
          categoryName: c.categoryName,
          amount: c.amount,
          accumDepAccountId: cat?.accumDepAccountId || null,
          accumDepAccountCode: cat?.accumDepAccountCode || '',
        };
      });

      await postDepreciationRun(activeCompany.id, {
        periodKey,
        periodLabel,
        docDate: asOfDate,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.id : null,
        branchName: activeBranch?.id && activeBranch.id !== 'all' ? activeBranch.branchName : null,
        userId: currentUser.uid,
        depExpenseAccount,
        categoryLines,
        runSnapshot: includedRows.map(r => {
          const part = apprPartOf(r);
          return { assetId: r.assetId, assetCode: r.assetCode, assetName: r.assetName, amount: r.editAmount, newNBV: r.newNBV, ...(part > 0 ? { appreciationPart: part } : {}) };
        }),
      });

      await applyDepreciationToAssets(activeCompany.id, includedRows.map(r => ({
        assetId: r.assetId,
        amount: r.editAmount,
        newAccumulated: r.newAccumulated,
        newNBV: r.newNBV,
        fullyDepreciated: r.fullyDepreciated,
        ...(r.apprDepBefore != null ? { newAppreciationDepreciation: round2(r.apprDepBefore + apprPartOf(r)) } : {}),
      })), asOfDate);

      alert(`Posted depreciation of ${formatCurrency(grandTotal)} for ${periodLabel}.`);
      setRows(null);
      setExistingRun(null);
      loadRuns();
    } catch (e) {
      console.error('Post failed:', e);
      alert(`Failed to post depreciation: ${e.message}`);
    } finally {
      setPosting(false);
    }
  };

  const years = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 6; y--) years.push(y);

  return (
    <PageLayout pageTitle="Depreciation Run" breadcrumb="Fixed Assets / Depreciation">
      <ContentCard>
        <PeriodRow>
          <FormGroup style={{ minWidth: 150 }}>
            <Label>Frequency</Label>
            <Select value={frequency} onChange={(e) => { setFrequency(e.target.value); setRows(null); }}>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </Select>
          </FormGroup>
          {frequency === 'monthly' && (
            <FormGroup style={{ minWidth: 150 }}>
              <Label>Month</Label>
              <Select value={month} onChange={(e) => { setMonth(Number(e.target.value)); setRows(null); }}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </Select>
            </FormGroup>
          )}
          <FormGroup style={{ minWidth: 120 }}>
            <Label>Year</Label>
            <Select value={year} onChange={(e) => { setYear(Number(e.target.value)); setRows(null); }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
          </FormGroup>
          <GenerateBtn onClick={() => { setEditingRun(null); handleGenerate(); }} disabled={generating}>
            <FiPlay /> {generating ? 'Generating...' : 'Generate'}
          </GenerateBtn>
        </PeriodRow>
      </ContentCard>

      {editingRun && (
        <Banner>
          <FiEdit2 /> Editing <strong>&nbsp;{editingRun.docNumber}&nbsp;</strong> ({editingRun.periodLabel || editingRun.periodKey}). Change amounts or untick assets, then Save Changes.
          <FormGroup style={{ margin: '0 0 0 12px', minWidth: 160 }}>
            <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
          </FormGroup>
          <SmallBtn onClick={cancelEditRun}><FiX /> Cancel</SmallBtn>
        </Banner>
      )}

      {existingRun && (
        <Banner>
          <FiAlertTriangle /> A depreciation run for <strong>&nbsp;{periodLabel}&nbsp;</strong> was already posted ({existingRun.docNumber}). Posting again will double-charge.
        </Banner>
      )}

      {rows !== null && (
        <>
          <StatsRow>
            <StatCard>
              <StatIcon $color={colors.primary.main}><FiCalendar /></StatIcon>
              <StatInfo><StatValue>{includedRows.length}</StatValue><StatLabel>Assets ({periodLabel})</StatLabel></StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon $color={colors.info.main}><FiCheckCircle /></StatIcon>
              <StatInfo><StatValue>{formatCurrency(grandTotal)}</StatValue><StatLabel>Total Depreciation</StatLabel></StatInfo>
            </StatCard>
          </StatsRow>

          <ContentCard>
            {rows.length === 0 ? (
              <EmptyMsg>No active depreciable assets for {periodLabel}.</EmptyMsg>
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <Th>Include</Th>
                      <Th>Code</Th>
                      <Th>Asset</Th>
                      <Th>Category</Th>
                      <Th>Method</Th>
                      <Th style={{ textAlign: 'right' }}>Opening NBV</Th>
                      <Th style={{ textAlign: 'right' }}>Depreciation</Th>
                      <Th style={{ textAlign: 'right' }}>Closing NBV</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <Tr key={r.assetId} style={{ opacity: r.include ? 1 : 0.5 }}>
                        <Td><input type="checkbox" checked={r.include} disabled={r.disposed} title={r.disposed ? 'Disposed — reverse its disposal to change' : ''} onChange={() => toggleInclude(r.assetId)} /></Td>
                        <Td><strong>{r.assetCode}</strong></Td>
                        <Td>{r.assetName}</Td>
                        <Td>{r.categoryName}</Td>
                        <Td>{r.method === 'reducing_balance' ? 'WDV' : r.method === 'straight_line' ? 'SLM' : '—'}</Td>
                        <Td style={{ textAlign: 'right' }}>{formatCurrency(r.opening)}</Td>
                        <Td style={{ textAlign: 'right' }}>
                          <AmountInput type="number" min="0" step="0.01" value={r.editAmount}
                            disabled={!r.include || r.disposed}
                            onChange={(e) => setRowAmount(r.assetId, e.target.value)} />
                        </Td>
                        <Td style={{ textAlign: 'right' }}>{formatCurrency(r.newNBV)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>

                <CategorySummary>
                  {Object.values(byCategory).map(c => (
                    <SummaryChip key={c.categoryId || 'uncat'}>
                      <span>{c.categoryName}</span>
                      <strong>{formatCurrency(c.amount)}</strong>
                    </SummaryChip>
                  ))}
                </CategorySummary>

                <PostRow>
                  <span>Posts: <strong>Dr Depreciation Expense</strong> {formatCurrency(grandTotal)} / <strong>Cr Accumulated Depreciation</strong> per category.</span>
                  {canEdit && (
                    <SaveButton $approve onClick={handlePost} disabled={posting || grandTotal <= 0}>
                      <FiSave /> {posting ? 'Saving...' : (editingRun ? `Save Changes (${formatCurrency(grandTotal)})` : `Post ${formatCurrency(grandTotal)}`)}
                    </SaveButton>
                  )}
                </PostRow>
              </>
            )}
          </ContentCard>
        </>
      )}
      <ContentCard style={{ marginTop: 16 }}>
        <h4 style={{ margin: '0 0 12px', color: colors.text.primary }}>Posted Depreciation Runs</h4>
        {runs.length === 0 ? (
          <EmptyMsg>No depreciation runs posted yet.</EmptyMsg>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Voucher</Th>
                <Th>Period</Th>
                <Th>Date</Th>
                <Th style={{ textAlign: 'right' }}>Assets</Th>
                <Th style={{ textAlign: 'right' }}>Amount</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {runs.map(run => (
                <Tr key={run.id} style={editingRun?.id === run.id ? { background: `${colors.primary.main}12` } : undefined}>
                  <Td><strong>{run.docNumber}</strong></Td>
                  <Td>{run.periodLabel || run.periodKey}</Td>
                  <Td>{run.docDate}</Td>
                  <Td style={{ textAlign: 'right' }}>{(run.depreciationSnapshot || []).length}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatCurrency(run.totalDebit || 0)}</Td>
                  <Td>
                    {canEdit && <SmallBtn onClick={() => startEditRun(run)} title="Edit"><FiEdit2 /></SmallBtn>}
                    {canDelete && <SmallBtn $danger onClick={() => handleDeleteRun(run)} title="Delete" style={{ marginLeft: 6 }}><FiTrash2 /></SmallBtn>}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </ContentCard>
    </PageLayout>
  );
};

const SmallBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px; padding: 6px 8px; font-size: 13px;
  background: none; border: 1px solid ${p => p.$danger ? colors.error.main : colors.border.light}; border-radius: 6px;
  color: ${p => p.$danger ? colors.error.main : colors.text.secondary}; cursor: pointer;
  &:hover { border-color: ${p => p.$danger ? colors.error.dark : colors.primary.main}; color: ${p => p.$danger ? colors.error.dark : colors.primary.main}; }
`;

const PeriodRow = styled.div`display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap;`;
const GenerateBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; height: 40px;
  background: ${colors.primary.main}; color: #fff; border: none; border-radius: 8px; cursor: pointer; font-size: 14px;
  &:disabled { opacity: 0.6; cursor: default; }
`;
const Banner = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 12px 16px; margin-bottom: 16px;
  background: ${colors.warning.main}1a; border: 1px solid ${colors.warning.main}; border-radius: 8px;
  color: ${colors.warning.dark}; font-size: 14px;
`;
const EmptyMsg = styled.div`padding: 40px 20px; text-align: center; color: ${colors.text.secondary};`;
const AmountInput = styled.input`
  width: 120px; text-align: right; padding: 6px 8px; border: 1px solid ${colors.border.light}; border-radius: 6px;
  font-size: 13px; background: ${colors.background.main}; color: ${colors.text.primary};
  &:disabled { background: ${colors.background.paper}; }
`;
const CategorySummary = styled.div`display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px;`;
const SummaryChip = styled.div`
  display: flex; gap: 8px; align-items: center; padding: 6px 12px; border: 1px solid ${colors.border.light};
  border-radius: 20px; font-size: 13px; color: ${colors.text.secondary};
  strong { color: ${colors.text.primary}; }
`;
const PostRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 20px;
  padding-top: 16px; border-top: 1px solid ${colors.border.light}; font-size: 13px; color: ${colors.text.secondary};
  flex-wrap: wrap;
`;

export default DepreciationRun;
